// =============================================================================
// Admin Sidebar — Navigation with collapsible sections
// =============================================================================

import { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useApiClient } from '@tkpod/api-client';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavChild {
  name: string;
  href: string;
  requiredPermissions?: string[];
  /** Match this link only on the exact path (for hrefs that prefix siblings). */
  end?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.FC<{ className?: string }>;
  requiredPermissions?: string[];
  children?: NavChild[];
  /** Renders a live count badge next to the item name. */
  badge?: 'anomalies';
  /**
   * Hidden for provider-scoped users even when their permissions match —
   * the backing endpoints reject provider JWTs (default-closed RolesGuard
   * rule), so showing the link would only produce a 403 on click.
   */
  hideForProvider?: boolean;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: DashboardIcon, requiredPermissions: ['analytics:read'] },
  { name: 'Tenants', href: '/tenants', icon: TenantsIcon, requiredPermissions: ['organization:list'] },
  { name: 'Orders', href: '/orders', icon: OrdersIcon, requiredPermissions: ['order:list'] },
  {
    name: 'Catalog',
    href: '/catalog',
    icon: ProductsIcon,
    requiredPermissions: ['product_template:list'],
    children: [
      { name: 'Templates', href: '/catalog', requiredPermissions: ['product_template:list'] },
      { name: 'Categories', href: '/catalog/categories', requiredPermissions: ['product_template:list'] },
    ],
  },
  // Color Library lives under Variant Options (header button on that page).
  { name: 'Variant Options', href: '/variant-options', icon: SlidersIcon, requiredPermissions: ['product_template:list'] },
  {
    name: 'Design Library',
    href: '/library',
    icon: ImageIcon,
    requiredPermissions: ['library:read'],
    children: [
      // `end`: /library is a prefix of the other library routes, so it must
      // only highlight on the exact path (not on /library/upload etc.).
      { name: 'Assets', href: '/library', requiredPermissions: ['library:read'], end: true },
      { name: 'Upload', href: '/library/upload', requiredPermissions: ['library:write'] },
      { name: 'Batches', href: '/library/uploads', requiredPermissions: ['library:read'] },
      { name: 'Excel Import', href: '/library/import', requiredPermissions: ['library:write'] },
      { name: 'Categories & Tags', href: '/library/taxonomy', requiredPermissions: ['library:read'] },
    ],
  },
  { name: 'Cost Manager', href: '/cost-manager', icon: PlansIcon, requiredPermissions: ['product_template:list'] },
  { name: 'Providers', href: '/providers', icon: ProvidersIcon, requiredPermissions: ['provider:list'] },
  { name: 'Plans', href: '/plans', icon: PlansIcon, requiredPermissions: ['settings:manage'] },
  {
    name: 'Billing',
    href: '/wallets',
    icon: PlansIcon,
    hideForProvider: true,
    requiredPermissions: [
      'wallet:adjust',
      'subscription:manage',
      'audit_log:list',
      'order:list',
    ],
    children: [
      { name: 'Wallets', href: '/wallets', requiredPermissions: ['wallet:adjust'] },
      { name: 'Tax exemptions', href: '/billing/tax-exemptions', requiredPermissions: ['wallet:adjust'] },
      { name: 'Subscriptions', href: '/subscriptions', requiredPermissions: ['subscription:manage'] },
      { name: 'Failed Payments', href: '/billing/failed-payments', requiredPermissions: ['order:list'] },
      { name: 'Reconciliation', href: '/reconciliation', requiredPermissions: ['audit_log:list'] },
    ],
  },
  { name: 'Jobs', href: '/jobs', icon: JobsIcon, requiredPermissions: ['job:list'] },
  { name: 'Anomalies', href: '/anomalies', icon: AlertIcon, requiredPermissions: ['anomaly:list'], badge: 'anomalies' },
  {
    name: 'Notifications',
    href: '/notifications/broadcasts',
    icon: BellIcon,
    requiredPermissions: ['notification:list'],
    children: [
      { name: 'Announcements', href: '/notifications/broadcasts', requiredPermissions: ['notification:list'] },
      { name: 'Automations', href: '/notifications/automations', requiredPermissions: ['notification:list'] },
      { name: 'Branding', href: '/notifications/branding', requiredPermissions: ['notification:read'] },
    ],
  },
  {
    name: 'User Management',
    href: '/admin-users',
    icon: UsersIcon,
    requiredPermissions: ['admin_user:list'],
    children: [
      { name: 'Admin Users', href: '/admin-users', requiredPermissions: ['admin_user:list'] },
      { name: 'Roles', href: '/roles', requiredPermissions: ['admin_role:list'] },
      { name: 'Portal Roles', href: '/portal-roles', requiredPermissions: ['admin_role:list'] },
      { name: 'Platform Users', href: '/platform-users', requiredPermissions: ['user:list'] },
    ],
  },
  { name: 'Settings', href: '/settings', icon: SettingsIcon, requiredPermissions: ['settings:read'] },
];

function checkPermission(userPerms: string[], required?: string[]): boolean {
  if (!required || required.length === 0) return true;
  return hasAnyPermission(userPerms, required);
}

export function AdminSidebar({ open, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const userPermissions = user?.permissions ?? user?.currentOrganization?.permissions ?? [];
  const isProvider = !!user?.providerId;

  const visibleNavigation = useMemo(() => {
    return navigation
      .filter((item) => !(isProvider && item.hideForProvider))
      .filter((item) => checkPermission(userPermissions, item.requiredPermissions))
      .map((item) => {
        if (!item.children) return item;
        const visibleChildren = item.children.filter((child) =>
          checkPermission(userPermissions, child.requiredPermissions),
        );
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      })
      .filter(Boolean) as NavItem[];
  }, [userPermissions, isProvider]);

  return (
    <aside
      className={clsx(
        'fixed inset-y-0 left-0 z-40 w-64 bg-brand-950 border-r border-brand-900',
        'transform transition-transform duration-200 ease-in-out',
        'lg:relative lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-brand-900 px-6">
        <div className="h-8 w-8 rounded-lg bg-brand-500 flex items-center justify-center">
          <span className="text-sm font-bold text-white">TK</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">Transfer Kingdom</p>
          <p className="text-xs text-brand-300">Admin Panel</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {visibleNavigation.map((item) =>
          item.children ? (
            <CollapsibleNavItem
              key={item.name}
              item={item}
              currentPath={location.pathname}
              onClose={onClose}
            />
          ) : (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                  'transition-colors duration-150',
                  isActive
                    ? 'bg-brand-800 text-white'
                    : 'text-brand-200 hover:bg-brand-900 hover:text-white',
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="flex-1">{item.name}</span>
              {item.badge === 'anomalies' && <AnomalyCountBadge />}
            </NavLink>
          ),
        )}
      </nav>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Anomaly count badge — open anomaly total from the ops-health summary
// ---------------------------------------------------------------------------
// Only mounted when the user holds anomaly:list (nav filtering guarantees
// this), so the summary endpoint is never called without permission.

interface AnomalySummary {
  openTotal: number;
  bySeverity: Record<string, number>;
  overdueTicks: string[];
}

function AnomalyCountBadge() {
  const api = useApiClient();
  const summaryQuery = useQuery<AnomalySummary>({
    queryKey: ['admin', 'anomaly-summary'],
    queryFn: () => api.get<AnomalySummary>('/api/v1/admin/anomalies/summary'),
    refetchInterval: 60_000,
  });

  const openTotal = summaryQuery.data?.openTotal ?? 0;
  if (openTotal === 0) return null;

  const critical = (summaryQuery.data?.bySeverity.CRITICAL ?? 0) > 0;
  return (
    <span
      className={clsx(
        'ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none',
        critical ? 'bg-red-500 text-white' : 'bg-amber-400 text-brand-950',
      )}
      aria-label={`${openTotal} open anomalies`}
    >
      {openTotal > 99 ? '99+' : openTotal}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Collapsible nav item with children
// ---------------------------------------------------------------------------

function CollapsibleNavItem({
  item,
  currentPath,
  onClose,
}: {
  item: NavItem;
  currentPath: string;
  onClose: () => void;
}) {
  const isChildActive = item.children?.some((child) =>
    currentPath.startsWith(child.href),
  );

  const [expanded, setExpanded] = useState(!!isChildActive);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={clsx(
          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
          'transition-colors duration-150',
          isChildActive
            ? 'bg-brand-800/50 text-white'
            : 'text-brand-200 hover:bg-brand-900 hover:text-white',
        )}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{item.name}</span>
        <ChevronIcon
          className={clsx(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            expanded && 'rotate-90',
          )}
        />
      </button>

      {expanded && item.children && (
        <div className="ml-4 mt-1 space-y-0.5 border-l border-brand-800 pl-4">
          {item.children.map((child) => (
            <NavLink
              key={child.href}
              to={child.href}
              end={child.end}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'block rounded-md px-3 py-1.5 text-sm',
                  'transition-colors duration-150',
                  isActive
                    ? 'font-medium text-white bg-brand-800'
                    : 'text-brand-300 hover:text-white hover:bg-brand-900',
                )
              }
            >
              {child.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
    </svg>
  );
}

function TenantsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
    </svg>
  );
}

function OrdersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>
  );
}

function ProductsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function JobsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008ZM6.75 17.25h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  );
}

function PlansIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
    </svg>
  );
}

function ProvidersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  );
}

function SlidersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}
